import React, {useState, useEffect} from 'react';
import {Button, Col, message, Modal, Row, Upload, Form, Select, Spin, Tooltip} from "antd";
import {InboxOutlined} from "@ant-design/icons";
import RequestedFile from "../Request/RequestFile";
import {languages} from "../../constants";
import {useAuth0} from "../../services/auth0";
import {getRevisedPresignUrl, uploadUserFile, updateRequest, getUsersByRole} from "../../services/api.service";
import {roles} from "../../constants";

const {Dragger} = Upload;

const layout = {
    labelCol: {span: 7},
    wrapperCol: {span: 17},
};

const normFile = e => {
    if (Array.isArray(e)) {
        return e;
    }
    return e && e.fileList;
};

const EditRequest = ({modalState, toggleState, data}) => {
    const {user, getIdTokenClaims} = useAuth0();
    const [submitLoader, setLoader] = useState(false);
    const [fileData, setFile] = useState([]);
    const [reviseType, setReviseType] = useState(null);
    const [drpLoader, setDrpLoader] = useState(false);
    const [usersList, setUsersList] = useState([]);
    const [form] = Form.useForm();
    const [role, setRole] = useState('');


    const props = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        onRemove: file => {
            setFile([]);
        },
        beforeUpload: file => {
            if (reviseType) {
                if (reviseType === 'revisedTranscript' || reviseType === 'revisedTranslation') {
                    const fileRegex = /(.json|.txt|.doc|.docx|.ppt|.pptx)$/;
                    if (fileRegex.test(file.name)) {
                        setFile([file]);
                    } else {
                        message.warn('Please select Json/text file')
                    }
                } else if (reviseType === 'finalOutput') {
                    const fileRegex = /(.avi|.wmv|.flv|.mpg|.mp4|.mp3|.m4a|.doc|.txt|.docx|.mov|.pdf|.ppt|.pptx)$/;
                    if (fileRegex.test(file.name)) {
                        setFile([file]);
                    } else {
                        message.warn('Please select audio,video,document or text file')
                    }
                }
            } else {
                form.submit();
            }
            return false;
        }
    };

    useEffect(() => {
        getUsers();
        getUserRole();
    }, [])

    const getUsers = async () => {
        try {
            setDrpLoader(true);
            const {data: {data: users}} = await getUsersByRole('translator');
            setUsersList(users);
            setDrpLoader(false);
        } catch (err) {
            setDrpLoader(false);
        }
    }

    const getUserRole = async () => {

        const claims = await getIdTokenClaims();

        const role = claims ? claims['https://paradi.io/access'] : localStorage.getItem('role'); //occasionally getIdTokenClaim call fails, so fall back to local Storage in that case
        setRole(role);
    }


    const handleCancel = () => {
        toggleState({}, false);
    };

    const onRequestSubmit = async (requestData) => {
        try {
            if (!fileData.length && !requestData.userId) {
                message.error('Please select a file');
                return;
            }
            setLoader(true);
            if (fileData.length) {
                const presignUrlData = await getPresignUrl(fileData[0], requestData.reviseOutput);
                await uploadFile(presignUrlData, fileData[0]);
                switch (requestData.reviseOutput) {
                    case 'revisedTranscript':
                        data.revisedTranscriptLink = `s3://${presignUrlData.fields.bucket}/${presignUrlData.fields.key}`;
                        data.requestStatus = 'human transcribed';
                        break;
                    case 'revisedTranslation':
                        data.revisedTranslationLink = `s3://${presignUrlData.fields.bucket}/${presignUrlData.fields.key}`;
                        data.requestStatus = 'human translated';
                        break;
                    case 'finalOutput':
                        data.outputLink = `s3://${presignUrlData.fields.bucket}/${presignUrlData.fields.key}`;
                        data.requestStatus = 'done';
                        break;
                }
            }
            data.assignee = requestData.userId || '';
            const {Attributes} = await requestTranslationRequest(data);
            setLoader(false);
            toggleState(Attributes, false);
            message.success('Request has been successfully updated');
        } catch (err) {
            console.log(err, "err")
            setLoader(false);
            message.error('fail to process request');
        }
    };

    const getPresignUrl = async (selectedFile, reviseOutPut) => {
        try {
            const obj = {
                name: selectedFile.name.replace(/ +?/g, ''),
                type: selectedFile.type,
                reviseOutPut
            };
            const {data} = await getRevisedPresignUrl(obj);
            if (data.err) {
                throw new Error('Failed to send request')
            }
            return data.data;
        } catch (e) {
            throw new Error('Failed to send request')
        }
    };

    const uploadFile = async (s3Data, file) => {
        console.log('this is the file in the upload file method: ', file);
        const formData = new FormData();
        Object.keys(s3Data.fields).forEach(key => {
            formData.append(key, s3Data.fields[key]);
        });
        formData.append("file", file);
        return await uploadUserFile(s3Data.url, formData);
    };

    const requestTranslationRequest = async (reqData) => {
        delete reqData.status;
        delete reqData.updatedAt;
        const {data} = await updateRequest(reqData);
        if (data.error)
            throw new Error(data.error);
        else
            return data.data;
    };

    const removeFileHandler = () => {
        setFile([]);
    };

    const reviseChangeHandler = (value) => {
        setReviseType(value);
        setFile([]);
    };

    return (
        <Modal
            visible={modalState}
            title="Translation Request"
            width={800}
            maskClosable={false}
            onCancel={handleCancel}
            footer={[
                <Button form="request" key="submit" type="primary"
                        htmlType="submit" loading={submitLoader}>
                    Submit
                </Button>,
                <Button key="reset" onClick={handleCancel}>
                    Cancel
                </Button>
            ]}>
            <Form
                {...layout}
                id="request"
                name="requestForm"
                form={form}
                onFinish={onRequestSubmit}>
                <Row gutter={[8, 8]}>
                    <Col lg={8} xs={24}>
                        <Form.Item name="dragger" valuePropName="fileList" getValueFromEvent={normFile} noStyle>
                            <Dragger {...props}
                                     accept={['audio/*', 'video/*', '.doc', '.docx', '.txt', '.json', '.pdf', '.ppt', '.pptx']}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined/>
                                </p>
                                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                                <p className="ant-upload-hint">
                                    Audio/video/Document/Text file only
                                </p>
                            </Dragger>
                        </Form.Item>
                        {fileData.length ?
                            <RequestedFile fileName={fileData[0].name} removeFile={removeFileHandler}/> : null}
                    </Col>
                    <Col xs={24} lg={{span: 14, offset: 2}}>
                        {
                            role === roles.ADMIN ?
                                (<Spin spinning={drpLoader}>
                                    <Form.Item
                                        label="Translator Users"
                                        name="userId"
                                        rules={[{required: false, message: 'Please select translator user!'}]}
                                    >
                                        <Select placeholder="Select" defaultValue={data.assignee || ''}>
                                            {usersList.map(user => (
                                                <Select.Option key={user.id} value={user.id}>
                                                    <Tooltip placement="left" title={user.email}>
                                                        <div className="flex-container">
                                                            <div>{user.firstName} {user.lastName}</div>
                                                            <div style={{color: '#f52b25'}}>{user.spokenLanguages.join(',')}</div>
                                                        </div>
                                                    </Tooltip>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Spin>)
                                : <></>
                        }
                        <Form.Item
                            label="RevisedOutput"
                            name="reviseOutput"
                            rules={[{required: localStorage.role !== roles.ADMIN, message: 'Please select source language!'}]}
                        >
                            <Select placeholder="Select" onChange={reviseChangeHandler}>
                                <Select.Option value="revisedTranscript">Revised Transcript</Select.Option>
                                <Select.Option value="revisedTranslation">Revised Translation</Select.Option>
                                <Select.Option value="finalOutput">Final Output</Select.Option>
                            </Select>
                        </Form.Item>
                        <label>sourceLanguage</label> : {<GetLanguageName lang={data.sourceLanguage}/>}
                        <br/>
                        <label>targetLanguage</label> : {<GetLanguageName lang={data.targetLanguage}/>}
                        <br/>
                        <label>output</label> : {data.output}
                    </Col>
                </Row>
            </Form>
        </Modal>
    )
};

export default EditRequest;


export const GetLanguageName = ({lang}) => {
    const sourceLanguage = languages.find((lg) => {
        return lg.language === lang;
    });
    return (
        <span>{sourceLanguage ? sourceLanguage.name : lang}</span>
    )
};
